import React, { useState } from 'react';
import { Plus, Settings2, Shield, Users } from 'lucide-react';
import IdentityProviderSection from './AccessManagement/identityProviders/IdentityProviderSection';
import UsersDirectoryPanel from './AccessManagement/UsersDirectoryPanel';
import RolesPermissionsPanel from './AccessManagement/RolesPermissionsPanel';
import AccessIdentityOverview from './AccessManagement/AccessIdentityOverview';
import { useAccessTheme } from './AccessManagement/useAccessTheme';
import { logAccess } from './AccessManagement/accessSettingsLogger';
import {
  mockRoutes,
  type RoutePrivilege,
} from './Access/routePrivilegeTemplates';

export type { RoutePrivilege };
export { mockRoutes };

type AccessTab = 'users' | 'roles' | 'identity';

const TABS: { id: AccessTab; label: string; icon: typeof Users }[] = [
  { id: 'users', label: 'Users', icon: Users },
  { id: 'roles', label: 'Roles & Permissions', icon: Shield },
  { id: 'identity', label: 'Identity Providers', icon: Settings2 },
];

const AccessSettings = () => {
  const t = useAccessTheme();
  const [tab, setTab] = useState<AccessTab>('users');
  const [openAddUser, setOpenAddUser] = useState(false);
  const [openAddRole, setOpenAddRole] = useState(false);
  const [showAddProviderModal, setShowAddProviderModal] = useState(false);
  const [focusAdmins, setFocusAdmins] = useState(false);

  const selectTab = (next: AccessTab, admins = false) => {
    setTab(next);
    setFocusAdmins(next === 'users' && admins);
    setOpenAddUser(false);
    setOpenAddRole(false);
    setShowAddProviderModal(false);
    logAccess('app', 'tab_changed', { tab: next, focusAdmins: next === 'users' && admins });
  };

  return (
    <div className="w-full space-y-5">
      <AccessIdentityOverview
        onManageUsers={() => selectTab('users', false)}
        onViewAdmins={() => selectTab('users', true)}
        onManageRoles={() => selectTab('roles')}
        onSetupSso={() => selectTab('identity')}
        tabs={
          <div className={`flex gap-1 border-b ${t.border}`}>
            {TABS.map((item) => {
              const Icon = item.icon;
              const active = tab === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => selectTab(item.id, false)}
                  className={`inline-flex items-center gap-2 px-3 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    active
                      ? `${t.accentText} border-indigo-500`
                      : `${t.muted} border-transparent ${t.tabHover}`
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {item.label}
                </button>
              );
            })}
          </div>
        }
      />

      {tab === 'users' ? (
        <UsersDirectoryPanel
          openAddUser={openAddUser}
          onOpenAddUserChange={setOpenAddUser}
          focusAdmins={focusAdmins}
          onClearAdminFocus={() => setFocusAdmins(false)}
        />
      ) : null}
      {tab === 'roles' ? (
        <RolesPermissionsPanel openAddRole={openAddRole} onOpenAddRoleChange={setOpenAddRole} />
      ) : null}
      {tab === 'identity' ? (
        <div className="space-y-3">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => {
                setShowAddProviderModal(true);
                logAccess('app', 'add_identity_provider_opened');
              }}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg shadow-sm ${t.btnPrimary}`}
            >
              <Plus className="w-4 h-4" /> Identity Provider
            </button>
          </div>
          <IdentityProviderSection
            showHeader={false}
            addModalOpen={showAddProviderModal}
            onOpenAddModal={() => setShowAddProviderModal(true)}
            onCloseAddModal={() => setShowAddProviderModal(false)}
          />
        </div>
      ) : null}
    </div>
  );
};

export default AccessSettings;
